import { useTranslation } from 'react-i18next'
import LoadingSkeleton from '../../../components/ui/LoadingSkeleton'
import { useActionDetail } from '../hooks/useActionDetail'
import { GRANTS } from '../../../auth/grants'
import { useGrants } from '../../../auth/useCurrentUser'
import ActionDocumentsSection from './ActionDocumentsSection'
import ActionCompletionNote from './ActionCompletionNote'
import ActionInfoGrid from './ActionInfoGrid'
import { ActionNotesTable } from './ActionNotesSection'
import ReadOnlySubTaskList from './ReadOnlySubTaskList'
import ActionPeopleEditor from './ActionPeopleEditor'
import ActionTerminCard from './ActionTerminCard'
import SinglePersonEditor from './SinglePersonEditor'
import TaskClassificationView from './TaskClassificationView'
import { mainResponsibleOf, secondaryResponsiblesOf } from '../types'
import '../../minutes/minutes.css'
import '../../meetings/meetings.css'
import '../actions.css'

interface Props {
  /** Gösterilecek ana görevin kimliği. */
  actionId: number
  /**
   * Veri yalnız gerektiğinde çekilsin diye: `false` iken hiçbir ağ isteği atılmaz.
   *
   * Pop-up içinde kullanıldığında "pop-up açık mı", Yeniden Açma pop-up'ında ise "bu görev şu an
   * seçili mi" anlamına gelir; kapalı bir yüzey ağ trafiği üretmez.
   */
  active?: boolean
  /**
   * Verilirse alt görev satırları tıklanabilir olur (Toplantı Yönetimi görev detay pop-up'ı — alt
   * görevi yeni sekmede açar). Verilmezse aşağıdaki "gezinme yok" sözleşmesi aynen geçerlidir.
   */
  onOpenSubTask?: (id: number) => void
}

/**
 * <b>Ana Görev Detayı</b> — <b>salt-okuma</b> gövde (105'te ortaklaştırıldı).
 *
 * <p><b>İki yüzey bunu paylaşır</b> ve ikisi de aynı şeyi göstermek zorundadır:
 * <ul>
 *   <li>{@code MainTaskViewDialog} — Alt Görev Detay ekranındaki "Ana Görevi Gör" pop-up'ı;
 *   <li>Toplantı Yönetimi'ndeki <b>Yeniden Açma</b> pop-up'ının sağ bölmesi (spec FR-036: "detay
 *       yapısı mevcut Ana Görevi Gör ekranıyla AYNI olmalı ve yeniden kullanılmalı").
 * </ul>
 *
 * <p><b>Neden ortaklaştırıldı:</b> ikinci bir kopya zamanla sessizce ayrışırdı — kod büyük ölçüde AI
 * ile üretildiği için her oturum biraz farklı bir detay görünümü üretme eğilimindedir ve Reuse
 * Mandate'in gerekçesi tam olarak budur. Ortaklaştırma kopya sayısını <b>artırmaz, azaltır</b>:
 * mevcut pop-up da artık bu kaynaktan beslenir.
 *
 * <p><b>Sözleşme: SALT-OKUMA ve GEZİNME YOK.</b> Alt görev satırları tıklanamaz, notlarda
 * Düzenle/Sil kolonu çizilmez, dokümanlarda yalnız indirme kalır (indirme bir OKUMA eylemidir —
 * sayfa değiştirmez). Hedef teslim tarihi kartında talep akışı yoktur: {@code
 * onRequestDeadlineUpdate} verilmediği için buton hiç çizilmez.
 *
 * <p><b>Tek istisna — {@code onOpenSubTask}</b> (02.10.2026): Toplantı Yönetimi'nin görev detay
 * pop-up'ı alt görev satırlarını <b>yeni sekmede</b> açar. Yalnız o yüzey prop'u geçer; bu bir
 * sayfa değişimi değildir (pop-up yerinde kalır), salt-okuma sözü de bozulmaz.
 */
export default function MainTaskDetailView({
  actionId,
  active = true,
  onOpenSubTask,
}: Readonly<Props>) {
  const { t } = useTranslation()
  const { hasGrant } = useGrants()
  const { data, isLoading, isError } = useActionDetail(active ? actionId : null)

  const meetingBound = data?.meetingId != null
  const mainResponsible = mainResponsibleOf(data?.assignees)
  const secondaryResponsibles = secondaryResponsiblesOf(data?.assignees)

  if (isLoading) {
    return <LoadingSkeleton />
  }
  if (isError) {
    return <p className="act-empty-hint">{t('actions.detail.status.error')}</p>
  }
  if (!data) {
    return null
  }

  return (
    <div className="mtg-detail act-maintask-view">
      {/*
        Termin kartı — talep akışı YOK: `onRequestDeadlineUpdate` verilmediği için buton hiç
        çizilmez (bileşenin sözleşmesi). Bu kullanıcı ana görevde talep giremez; buton çizilseydi
        uç 403 dönerdi.
      */}
      <ActionTerminCard deadline={data.deadline} statusId={data.statusId} />

      <section className="minutes-section">
        <div className="minutes-section-head">
          <h2>{t('actions.detail.info.title')}</h2>
          {data.overdueLabel && (
            <span className="act-overdue-label">{t('actions.status.overdueLabel')}</span>
          )}
        </div>
        <ActionInfoGrid
          data={data}
          meetingBound={meetingBound}
          urgencyName={data.urgencyLevelName ?? '—'}
          importanceName={data.importanceLevelName ?? '—'}
          statusId={data.statusId}
        />

        {/* Kişi bölümleri: editable=false → yalnız çip listesi, seçici/çıkar düğmesi yok. */}
        <div className="minutes-subsection">
          <h3 className="minutes-subhead">{t('actions.detail.mainResponsible.title')}</h3>
          <SinglePersonEditor
            person={mainResponsible}
            editable={false}
            canClear={false}
            onSet={noop}
            onClear={noop}
            placeholder=""
            emptyText={t('actions.detail.mainResponsible.empty')}
            removeLabel=""
          />
        </div>

        <div className="minutes-subsection">
          <h3 className="minutes-subhead">
            {t('actions.detail.secondaryResponsibles.title')}
          </h3>
          <ActionPeopleEditor
            people={secondaryResponsibles}
            editable={false}
            onAdd={noop}
            onRemove={noop}
            placeholder=""
            emptyText={t('actions.detail.secondaryResponsibles.empty')}
            removeLabel=""
          />
        </div>

        <div className="minutes-subsection">
          <h3 className="minutes-subhead">{t('actions.detail.followers.title')}</h3>
          <ActionPeopleEditor
            people={data.followers}
            editable={false}
            onAdd={noop}
            onRemove={noop}
            placeholder=""
            emptyText={t('actions.detail.followers.empty')}
            removeLabel=""
          />
        </div>
      </section>

      {/* Tamamlanma Notu — Görev Detay sayfasıyla AYNI bileşen ve aynı konum (kartın altı). */}
      <ActionCompletionNote note={data.completionNote} />

      {/*
        Açıklama — sayfadaki gibi devre-dışı `InputTextarea` DEĞİL, düz metin. Salt-okuma bir
        popup'ta griye boyanmış girdi kutuları hem yer harcar hem "burada bir şey yazabilirim"
        izlenimi verir; metin `pre-wrap` ile satır sonlarını korur.
      */}
      <section className="minutes-section">
        <div className="minutes-section-head">
          <h2>{t('actions.detail.desc.title')}</h2>
        </div>
        <div className="act-desc-grid">
          <div className="act-field">
            <span className="ui-field-label">{t('actions.form.name')}</span>
            <p className="act-readonly-text">{data.name}</p>
          </div>
          <div className="act-field">
            <span className="ui-field-label">{t('actions.detail.desc.detail')}</span>
            <p className="act-readonly-text">{data.description ?? '—'}</p>
          </div>
          {/* Stratejik Hedef · Katkı (127) — Görev Detay sayfasındaki alanların salt-okuma karşılığı. */}
          <TaskClassificationView
            strategicObjectives={data.strategicObjectives}
            contributions={data.contributions}
          />
        </div>
      </section>

      {/* Alt görevler — `onOpenSubTask` yoksa satırlar TIKLANAMAZ (gezinme yok); Sil kolonu ve
          "Alt Görev Ekle" hiçbir yüzeyde yok. */}
      <section className="minutes-section">
        <div className="minutes-section-head">
          <h2>{t('actions.detail.subActions.title')}</h2>
        </div>
        <ReadOnlySubTaskList
          subActions={data.subActions}
          showRecordNumber={hasGrant(GRANTS.RECORD_NUMBER_VIEW)}
          onOpen={onOpenSubTask}
        />
      </section>

      {/* Notlar — canManageNotes=false → Düzenle/Sil kolonu yok; "Not Ekle" butonu hiç yok. */}
      <section className="minutes-section">
        <div className="minutes-section-head">
          <h2>{t('actions.detail.notes.title')}</h2>
        </div>
        <ActionNotesTable
          notes={data.notes}
          canManageNotes={false}
          onEdit={noop}
          onDelete={noop}
        />
      </section>

      {/* Dokümanlar — editable=false → yükleme/kaldırma yok, indirme var (bileşen sözleşmesi). */}
      <ActionDocumentsSection actionId={actionId} documents={data.documents} editable={false} />
    </div>
  )
}

/** Salt-okuma bolumlerin zorunlu ama cagrilmayan geri-cagrilari. */
function noop() {}
